import { Clock3, DatabaseZap, Infinity, Languages, MessagesSquare } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { getHomeContent, type ExpandableFeatureId, type HomeFeature, type HomeFeatureId, type HomeLocale } from "../homeContent";
import { hongKongContactPath, hongKongDownloadPath } from "../routes";
import { reportLeadEntryConversion } from "../tracking";
import { DownloadLink } from "./DownloadLink";
import { HomeOptionIcon } from "./home/HomeOptionIcon";
import { HomeOptionShape } from "./home/HomeOptionShape";
import { HomeCardShape } from "./home/HomeCardShape";

const featureIcons = { always: Clock3, channels: MessagesSquare, languages: Languages, systems: DatabaseZap, free: Infinity };
const cardAngles = [-14, -7, 0, 7, 14];
const fanRadiusRatio = 1 / Math.sin(7 * Math.PI / 180);
const optionOffsetsWide: Record<number, number[]> = {
  3: [-6.9, 0, 6.9],
  4: [-10.35, -3.45, 3.45, 10.35],
};
const optionOffsetsCompact: Record<number, number[]> = {
  3: [-5.45, 0, 5.45],
  4: [-8.175, -2.725, 2.725, 8.175],
};

const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

function fanPosition(angle: number): CSSProperties {
  const radians = angle * Math.PI / 180;
  return { "--fan-x": Math.sin(radians), "--fan-y": Math.cos(radians), "--fan-angle": `${angle}deg` } as CSSProperties;
}

export function Hero({ lang }: { lang: HomeLocale }) {
  const { search } = useLocation();
  const content = useMemo(() => getHomeContent(lang), [lang]);
  const [expanded, setExpanded] = useState<ExpandableFeatureId | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  // On wide layouts the option pills fan out above their own feature card;
  // narrower layouts keep them centered so the group never leaves the fan.
  const [alignOptionsToCard, setAlignOptionsToCard] = useState(false);
  // Auto-rotation pauses while the visitor drives the fan with pointer or keyboard.
  const [paused, setPaused] = useState(false);
  const hoverTimerRef = useRef<number | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const featureRefs = useRef<Partial<Record<HomeFeatureId, HTMLButtonElement | null>>>({});
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // Keep the language choices in view whenever another capability is not being explored.
  const visibleGroup = expanded ?? "languages";
  const expandedFeature = content.features.find((item) => item.id === visibleGroup);

  function cancelHoverChange() {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }

  function previewFeature(item: HomeFeature) {
    cancelHoverChange();
    if (visibleGroup === item.id) return;
    // Crossing a neighboring card on the way to an option must not replace the menu.
    hoverTimerRef.current = window.setTimeout(() => {
      setExpanded(item.options ? item.id as ExpandableFeatureId : null);
      hoverTimerRef.current = null;
    }, 180);
  }

  useEffect(() => () => {
    if (hoverTimerRef.current !== null) window.clearTimeout(hoverTimerRef.current);
  }, []);

  // Pick the desktop fan geometry before the browser paints. A regular effect
  // briefly showed the compact offsets first, making the option row jump on refresh.
  useBrowserLayoutEffect(() => {
    const wideLayout = window.matchMedia("(min-width: 1024px)");
    const update = () => setAlignOptionsToCard(wideLayout.matches);
    update();
    wideLayout.addEventListener("change", update);
    return () => wideLayout.removeEventListener("change", update);
  }, []);

  // Cycle through the capability groups until the visitor takes over; on
  // resume the rotation continues from the group currently on display.
  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => {
      setExpanded((current) => {
        const ids = content.features.map((item) => item.id);
        return ids[(ids.indexOf(current ?? "languages") + 1) % ids.length] as ExpandableFeatureId;
      });
    }, 3000);
    return () => window.clearInterval(timer);
  }, [paused, content]);

  useEffect(() => {
    if (!expanded) return;
    const closeOutside = (event: PointerEvent) => { if (!stageRef.current?.contains(event.target as Node)) { setExpanded(null); setPaused(false); } };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [expanded]);

  function selectFeature(item: HomeFeature) {
    cancelHoverChange();
    setSelectedOptionId(null);
    setExpanded(item.options ? item.id as ExpandableFeatureId : null);
  }

  function handleFeatureKey(event: KeyboardEvent<HTMLButtonElement>, index: number, item: HomeFeature) {
    cancelHoverChange();
    let next: number | undefined;
    if (event.key === "ArrowRight") next = (index + 1) % content.features.length;
    if (event.key === "ArrowLeft") next = (index + content.features.length - 1) % content.features.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = content.features.length - 1;
    if (next !== undefined) {
      event.preventDefault();
      const nextFeature = content.features[next];
      setExpanded(nextFeature.options ? nextFeature.id as ExpandableFeatureId : null);
      featureRefs.current[nextFeature.id]?.focus();
    }
    if ((event.key === "ArrowUp" || event.key === "ArrowDown") && item.options) {
      event.preventDefault();
      setExpanded(item.id as ExpandableFeatureId);
      window.requestAnimationFrame(() => optionRefs.current[0]?.focus());
    }
  }

  function handleOptionKey(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    cancelHoverChange();
    const count = expandedFeature?.options?.length ?? 0;
    let next: number | undefined;
    if (event.key === "ArrowRight") next = (index + 1) % count;
    if (event.key === "ArrowLeft") next = (index + count - 1) % count;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = count - 1;
    if (next !== undefined) { event.preventDefault(); optionRefs.current[next]?.focus(); }
    if (event.key === "ArrowDown") { event.preventDefault(); featureRefs.current[visibleGroup]?.focus(); }
  }

  return (
    <section id="hero" className="ce-home-hero" aria-labelledby="ce-home-title">
      <div className="ce-home-hero-blob ce-home-hero-blob-purple" aria-hidden="true" />
      <div className="ce-home-hero-blob ce-home-hero-blob-pink" aria-hidden="true" />
      <div className="ce-home-intro">
        <h1 id="ce-home-title"><span>{content.headline[0]}</span><span className="ce-home-gradient">{content.headline[1]}</span></h1>
        <p className="ce-home-description">{content.description.map((line) => <span key={line}>{line}</span>)}</p>
        <div className="ce-home-hero-actions">
          <DownloadLink
            className="ce-home-hero-primary"
            to={hongKongDownloadPath("hero_primary_cta", "free", search)}
            onClick={() => reportLeadEntryConversion(search)}
          >
            免費開始
          </DownloadLink>
          <Link
            className="ce-home-hero-secondary"
            to={hongKongContactPath(undefined, "hk_home_hero_support", search)}
            onClick={() => reportLeadEntryConversion(search)}
          >
            聯絡我們，獲取更多支援
          </Link>
        </div>
      </div>
      <div className="ce-home-fan" id="capabilities" ref={stageRef} data-expanded={visibleGroup}
        style={{ "--ce-radius-ratio": fanRadiusRatio } as CSSProperties}
        onPointerEnter={() => setPaused(true)}
        onPointerLeave={(event) => { cancelHoverChange(); if (event.pointerType === "mouse" && !stageRef.current?.contains(document.activeElement)) setPaused(false); }}
        onFocus={() => setPaused(true)}
        onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) { cancelHoverChange(); setPaused(false); } }}
        onKeyDown={(event) => { if (event.key === "Escape") { event.preventDefault(); cancelHoverChange(); featureRefs.current[visibleGroup]?.focus(); setExpanded(null); } }}
      >
        {/* Drop shadows live in SVG filters referenced via filter:url(#…):
            Chrome's compositor fast path for CSS drop-shadow() misclips the
            filter output when the same element is rotated, cutting the fill
            along a straight screen-aligned edge (the "diagonal gradient"
            artifact). SVG filters rasterize in local space before the
            transform, so rotated cards/pills stay clean. */}
        <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
          <defs>
            <filter id="ce-shape-shadow-card" x="-40%" y="-50%" width="180%" height="220%">
              <feDropShadow dx="0" dy="4" stdDeviation="7" floodColor="#000000" floodOpacity="0.07" />
            </filter>
            <filter id="ce-shape-shadow-card-strong" x="-60%" y="-80%" width="220%" height="260%">
              <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#000000" floodOpacity="0.3" />
            </filter>
            <filter id="ce-shape-shadow-pill" x="-40%" y="-50%" width="180%" height="220%">
              <feDropShadow dx="0" dy="4" stdDeviation="7" floodColor="#000000" floodOpacity="0.07" />
            </filter>
            <filter id="ce-shape-shadow-pill-strong" x="-40%" y="-120%" width="180%" height="360%">
              <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#000000" floodOpacity="0.3" />
            </filter>
          </defs>
        </svg>
        <div className="ce-home-feature-row" role="group" aria-label={content.featureLabel}>
          {content.features.map((item, index) => {
            const Icon = featureIcons[item.id];
            return <button key={item.id} type="button" id={`ce-feature-${item.id}`} data-home-feature={item.id}
              ref={(element) => { featureRefs.current[item.id] = element; }} className="ce-home-feature"
              style={fanPosition(cardAngles[index])}
              aria-expanded={item.options ? visibleGroup === item.id : undefined}
              aria-controls={item.options ? `ce-options-${item.id}` : undefined}
              onPointerEnter={(event) => { if (event.pointerType === "mouse") previewFeature(item); }} onPointerLeave={cancelHoverChange}
              onClick={() => selectFeature(item)} onKeyDown={(event) => handleFeatureKey(event, index, item)}
            >
              <HomeCardShape />
              {/* 选中阴影的渐层：url() 滤镜之间无法 transition，靠这层
                  强阴影叠片的 opacity 淡入淡出实现缓慢渐变。 */}
              <HomeCardShape glow />
              <Icon className="ce-home-feature-icon" aria-hidden="true" strokeWidth={1.9} />
              <span className="ce-home-feature-title">{item.label}</span>
            </button>;
          })}
        </div>
        {content.features.filter((item) => item.options).map((item) => {
          const baseAngle = alignOptionsToCard ? cardAngles[content.features.indexOf(item)] : 0;
          return (
          <div key={item.id} id={`ce-options-${item.id}`} className={visibleGroup === item.id ? "ce-home-options is-visible" : "ce-home-options"} role="group" aria-labelledby={`ce-feature-${item.id}`} aria-hidden={visibleGroup !== item.id}
            data-option-count={item.options!.length} style={{ "--option-count": item.options!.length } as CSSProperties} onPointerEnter={cancelHoverChange}>
            {item.options!.map((option, index) => <button key={option.id} type="button" data-home-option={option.id}
              ref={(element) => { if (visibleGroup === item.id) optionRefs.current[index] = element; }}
              style={fanPosition(baseAngle + (alignOptionsToCard ? optionOffsetsWide : optionOffsetsCompact)[item.options!.length][index])}
              aria-pressed={selectedOptionId === option.id}
              onClick={() => setSelectedOptionId(option.id)} onKeyDown={(event) => handleOptionKey(event, index)}
            ><HomeOptionShape /><HomeOptionShape glow /><HomeOptionIcon id={option.id} /><span className="ce-home-option-label"><span>{option.label}</span>{option.detail && <span className="ce-home-option-detail">{option.detail}</span>}</span></button>)}
          </div>
          );
        })}
      </div>
    </section>
  );
}
